import { FlattenedMatter } from './aggregateTypes';
import { InsuranceCaseDocument } from '@shared/types';
import {
  UserDocument,
  MatterDocument,
  FirmDocument,
  EngagementLetterDocument,
} from '@shared/types';
import { FirmDatabase } from './firmDatabase';
import { EnrichedMatterContact } from './aggregateTypes';

export function makeFlattenedMatter(
  data: {
    matter: MatterDocument;
    firm: FirmDocument;
    contacts: EnrichedMatterContact[];
    responsibleAttorney: UserDocument | undefined;
    otherAssignee: UserDocument | undefined;
    engagementOriginator: UserDocument | undefined;
    referralOriginator: UserDocument | undefined;
    primaryClient: EnrichedMatterContact | undefined;
    primaryAdverseParty: EnrichedMatterContact | undefined;
    engagementLetters: EngagementLetterDocument[] | undefined;
    insuranceCases?: InsuranceCaseDocument[] | undefined;
  },
  db: FirmDatabase,
): FlattenedMatter {
  // Check required fields
  if (
    !data.matter.name ||
    !data.matter.state ||
    !data.matter.status ||
    !data.matter.matterType
  ) {
    throw new Error(
      `Missing required fields in matter data: ${[
        !data.matter.name && 'name',
        !data.matter.state && 'state',
        !data.matter.status && 'status',
        !data.matter.matterType && 'matterType',
      ]
        .filter(Boolean)
        .join(', ')}`,
    );
  }

  // Ensure db is initialized
  if (!db) {
    throw new Error('Database is not properly initialized');
  }

  // Use insurance cases for insurance matters (support multiple)
  const insuranceCases =
    data.matter.matterType === 'ARAG' ||
    data.matter.matterType === 'MetLife' ||
    data.matter.matterType === 'LegalEase' ||
    data.matter.matterType === 'OtherInsurance'
      ? (data.insuranceCases ?? [])
      : [];

  // Back-compat legacy single-case fields derived from first case if present
  const primaryInsuranceCase = insuranceCases[0];

  return new FlattenedMatter({
    id: data.matter.id,
    createdTimestamp: data.matter.createdTimestamp,
    practiceArea: data.matter.practiceArea,
    responsibleAttyHourlyRate: data.matter.responsibleAttyHourlyRate,
    retainer: data.matter.retainer,
    scope: data.matter.scope,
    schedulingEmailSent: data.matter.schedulingEmailSent,
    subStatusId: data.matter.subStatusId,
    trustBalance: data.matter.trustBalance,
    feeEarned: data.matter.feeEarned,
    organizationRepresentatives: data.matter.organizationRepresentatives,
    flatFeeAmt: data.matter.flatFeeAmt,
    insuranceCases,
    isUninvoiceable: data.matter.isUninvoiceable,
    contacts: data.contacts,
    primaryClient: data.primaryClient,
    primaryAdverseParty: data.primaryAdverseParty,
    matterRef: db.query.matter(data.matter.id),
    description: data.matter.description || '',
    matterType: data.matter.matterType,
    leadSource: data.matter.leadSourceId
      ? data.firm.leadSources?.find(
          (leadSource) => leadSource.id === data.matter.leadSourceId,
        )
      : undefined,
    name: data.matter.name,
    state: data.matter.state,
    status: data.matter.status,
    engagementOriginator: data.engagementOriginator,
    referralOriginator: data.referralOriginator,
    responsibleAttorney: data.responsibleAttorney,
    otherAssignee: data.otherAssignee,
    engagementLetters: data.engagementLetters,
    contingencyFees: data.matter.contingencyFees,
  });
}
